/**
 * Error Fallback Component
 * User-friendly error display with recovery options
 * 
 * @task FND-FE-003: Error Boundaries
 */

import React, { ErrorInfo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  BugReport as BugReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Report as ReportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error;
  /** React error info */
  errorInfo?: ErrorInfo | null;
  /** Callback to reset the error boundary */
  onReset?: () => void;
  /** Callback to retry (usually page reload) */
  onRetry?: () => void;
  /** Show compact version (for nested boundaries) */
  compact?: boolean;
  /** Additional context for debugging */
  context?: Record<string, unknown>;
}

/**
 * Error Fallback - User-friendly error display component
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
  onRetry,
  compact = false,
  context,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showDetails, setShowDetails] = React.useState(false);
  const [reportSent, setReportSent] = React.useState(false);

  const handleGoHome = (): void => {
    navigate('/');
    onReset?.();
  };

  const handleGoBack = (): void => {
    navigate(-1);
    onReset?.();
  };

  const handleReportError = (): void => {
    // TODO: Send error report to tracking service
    console.error('Error reported:', {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      context,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
    setReportSent(true);
  };

  // Compact version for nested boundaries
  if (compact) {
    return (
      <Alert 
        severity="error" 
        sx={{ m: 2 }}
        action={
          onReset && (
            <Button color="inherit" size="small" onClick={onReset}>
              Dismiss
            </Button>
          )
        }
      >
        <AlertTitle>Something went wrong</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          borderRadius: 3,
        }}
      >
        {/* Error Icon & Title */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <BugReportIcon sx={{ fontSize: 40, color: 'error.dark' }} />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We apologize for the inconvenience. Our team has been notified.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Error Message */}
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error Details</AlertTitle>
          {error.message || 'An unexpected error occurred'}
        </Alert>

        {/* Recovery Options */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
          {onRetry && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              size="large"
            >
              Reload Page
            </Button>
          )}
          {onReset && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              size="large"
            >
              Go Back
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            size="large"
          >
            Go Home
          </Button>
        </Box>

        {/* Report Error Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="text"
            startIcon={reportSent ? undefined : <ReportIcon />}
            onClick={handleReportError}
            disabled={reportSent}
            size="small"
          >
            {reportSent ? 'Report Sent - Thank You!' : 'Report this Error'}
          </Button>
        </Box>

        {/* Technical Details (Collapsible) */}
        {import.meta.env.DEV && (
          <>
            <Button
              fullWidth
              onClick={() => setShowDetails(!showDetails)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ justifyContent: 'center' }}
            >
              {showDetails ? 'Hide Technical Details' : 'Show Technical Details'}
            </Button>

            <Collapse in={showDetails}>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Error Stack:
                </Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {error.stack}
                </pre>

                {errorInfo?.componentStack && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Component Stack:
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {errorInfo.componentStack}
                    </pre>
                  </>
                )}

                {context && Object.keys(context).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Context:
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(context, null, 2)}
                    </pre>
                  </>
                )}
              </Box>
            </Collapse>
          </>
        )}

        {/* Helpful Links */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" gutterBottom>
          Need help?
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Try refreshing the page" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Go back and try a different action" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <HomeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Return to the dashboard and start over" />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

/**
 * Simple inline error fallback for smaller components
 */
export const InlineErrorFallback: React.FC<{
  error: Error;
  onRetry?: () => void;
}> = ({ error, onRetry }) => {
  return (
    <Alert
      severity="error"
      sx={{ m: 2 }}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry} startIcon={<RefreshIcon />}>
            Retry
          </Button>
        )
      }
    >
      {error.message}
    </Alert>
  );
};

/**
 * Toast-style error notification
 */
export const ToastError: React.FC<{
  error: Error;
  onDismiss?: () => void;
}> = ({ error, onDismiss }) => {
  return (
    <Alert
      severity="error"
      onClose={onDismiss}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        maxWidth: 400,
        zIndex: 9999,
      }}
    >
      <AlertTitle>Error</AlertTitle>
      {error.message}
    </Alert>
  );
};

export default ErrorFallback;
